// ============================================================================
// Componente: AutomacaoConferenciaPIX.js
// Objetivo: Interface interna para upload dos arquivos ATOS e Extratos PIX.
// Padrão visual: Financeiro / Verde-azulado
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import {
    QrCode,
    UploadCloud,
    Play,
    Loader2,
    AlertCircle,
    CheckCircle,
    FileSpreadsheet,
    Eraser,
    Building2
} from 'lucide-react';

const AutomacaoConferenciaPIX = () => {
    const corModulo = '#16a39c';
    const corModuloEscura = '#0f766e';
    const corFundoClaro = '#f0fdfa';
    const corBorda = '#99f6e4';

    const atosInputRef = useRef(null);
    const hungerInputRef = useRef(null);
    const pgInputRef = useRef(null);
    const gcInputRef = useRef(null);
    const pmInputRef = useRef(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const [arquivosAtos, setArquivosAtos] = useState([]);
    const [extratoHunger, setExtratoHunger] = useState(null);
    const [extratoPg, setExtratoPg] = useState(null);
    const [extratoGc, setExtratoGc] = useState(null);
    const [extratoPm, setExtratoPm] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando arquivos...');
    const [isPolling, setIsPolling] = useState(false);
    const [executionId, setExecutionId] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        let interval;

        if (isPolling && executionId) {
            interval = setInterval(async () => {
                try {
                    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
                    const res = await fetch(`/api/automacoes-financeiro/logs/${executionId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (!res.ok) return;

                    const data = await res.json();
                    if (data.logs && data.logs.length > 0) {
                        const lastLog = data.logs[data.logs.length - 1];
                        const cleanLog = String(lastLog)
                            .replace(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3}\s*-\s*INFO\s*-\s*|\[.*?\]\s*)/g, '')
                            .trim();

                        setStatusText(cleanLog || lastLog);

                        if (cleanLog.includes('PASSO 1')) setProgress(20);
                        else if (cleanLog.includes('PASSO 2')) setProgress(50);
                        else if (cleanLog.includes('PASSO 3')) setProgress(80);
                        else if (cleanLog.includes('FINALIZADO') || cleanLog.includes('Processo concluído')) setProgress(100);
                    }
                } catch (_) {}
            }, 1000);
        }

        return () => clearInterval(interval);
    }, [isPolling, executionId]);

    const limparInput = (ref) => {
        if (ref.current) ref.current.value = '';
    };

    const handleLimpar = () => {
        setArquivosAtos([]);
        setExtratoHunger(null);
        setExtratoPg(null);
        setExtratoGc(null);
        setExtratoPm(null);
        setError('');
        setSuccess('');
        setProgress(0);
        setStatusText('Aguardando arquivos...');
        setExecutionId(null);
        setIsPolling(false);
        setIsLoading(false);

        [atosInputRef, hungerInputRef, pgInputRef, gcInputRef, pmInputRef].forEach(limparInput);
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!arquivosAtos || arquivosAtos.length === 0) {
            setError('Anexe ao menos os arquivos da ATOS em CSV.');
            return;
        }

        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        if (!token) {
            setError('Sua sessão expirou ou você não está autenticado. Faça login novamente.');
            return;
        }

        setIsLoading(true);
        const newExecutionId = `PIX_${Date.now()}`;
        setExecutionId(newExecutionId);
        setIsPolling(true);
        setProgress(5);
        setStatusText('Organizando arquivos...');

        const formData = new FormData();
        formData.append('executionId', newExecutionId);
        arquivosAtos.forEach(file => formData.append('arquivos_atos', file));
        if (extratoHunger) formData.append('extrato_hunger', extratoHunger);
        if (extratoPg) formData.append('extrato_pg', extratoPg);
        if (extratoGc) formData.append('extrato_gc', extratoGc);
        if (extratoPm) formData.append('extrato_pm', extratoPm);

        try {
            const response = await fetch('/api/automacoes-financeiro/conferencia-pix', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) {
                let errData = {};
                try {
                    errData = await response.json();
                } catch (_) {}
                throw new Error(errData.error || 'Erro desconhecido ao processar a automação.');
            }

            const blob = await response.blob();
            const disposition = response.headers.get('Content-Disposition');
            let filename = 'Conferencia_PIX.xlsx';
            if (disposition && disposition.includes('filename=')) {
                filename = disposition.split('filename=')[1].replace(/["']/g, '');
            }

            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);

            setProgress(100);
            setStatusText('Excel baixado com sucesso!');
            setSuccess('Automação concluída! O download iniciou automaticamente.');

            setArquivosAtos([]);
            setExtratoHunger(null);
            setExtratoPg(null);
            setExtratoGc(null);
            setExtratoPm(null);
            [atosInputRef, hungerInputRef, pgInputRef, gcInputRef, pmInputRef].forEach(limparInput);
        } catch (err) {
            setError(err.message);
            setStatusText('Falha no processamento.');
        } finally {
            setIsLoading(false);
            setTimeout(() => setIsPolling(false), 3000);
        }
    };

    const getProgressBarColor = () => {
        if (error) return '#ef4444';
        if (success) return '#10b981';
        return corModulo;
    };

    const cardStyle = {
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: isMobile ? '20px' : '28px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)'
    };

    const uploadAreaStyle = {
        border: `2px dashed ${corBorda}`,
        borderRadius: '14px',
        padding: '30px 15px',
        textAlign: 'center',
        backgroundColor: '#f8fafc',
        cursor: isLoading ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative'
    };

    const FileInputBox = ({ title, label, inputRef, onChange, selectedFile }) => (
        <div style={{ ...uploadAreaStyle, padding: '20px 15px' }}>
            <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={onChange}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                disabled={isLoading}
            />

            <p style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '800', color: '#1e293b' }}>{title}</p>

            {!selectedFile ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <UploadCloud size={24} color="#94a3b8" />
                    <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: '#64748b' }}>{label}</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <FileSpreadsheet size={24} color={corModulo} />
                    <p style={{ margin: 0, fontSize: '12px', fontWeight: '800', color: corModuloEscura, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                        {selectedFile.name}
                    </p>
                </div>
            )}
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={cardStyle}>
                <form onSubmit={handleUpload}>
                    <div style={{ marginBottom: '30px' }}>
                        <div style={{ backgroundColor: corFundoClaro, borderLeft: `4px solid ${corModulo}`, padding: '16px 20px', borderRadius: '10px', marginBottom: '24px' }}>
                            <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: corModuloEscura, fontSize: '15px', fontWeight: '800', margin: 0 }}>
                                <QrCode size={18} /> 1. Arquivos de Vendas ATOS
                            </h4>
                            <p style={{ color: '#115e59', fontSize: '14px', lineHeight: '1.6', margin: '6px 0 0 0' }}>
                                Faça o upload de todos os arquivos CSV gerados pela plataforma ATOS.
                            </p>
                        </div>

                        <div style={uploadAreaStyle}>
                            <input
                                ref={atosInputRef}
                                type="file"
                                multiple
                                accept=".csv"
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                                onChange={(e) => setArquivosAtos(Array.from(e.target.files || []))}
                                disabled={isLoading}
                                required
                            />

                            {arquivosAtos.length === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ backgroundColor: '#f1f5f9', padding: '16px', borderRadius: '50%', color: '#94a3b8' }}>
                                        <UploadCloud size={32} />
                                    </div>
                                    <div>
                                        <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#334155' }}>Arraste ou clique para selecionar os arquivos CSV</p>
                                        <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>Este bloco é obrigatório.</p>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ backgroundColor: corFundoClaro, padding: '16px', borderRadius: '50%', color: corModulo }}>
                                        <FileSpreadsheet size={32} />
                                    </div>
                                    <div>
                                        <p style={{ margin: '0 0 4px 0', fontWeight: '800', color: '#0f172a' }}>Arquivos ATOS carregados</p>
                                        <p style={{ margin: 0, fontSize: '13px', fontWeight: '800', color: corModuloEscura }}>{arquivosAtos.length} arquivo(s) selecionado(s)</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ marginBottom: '30px' }}>
                        <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '20px' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Building2 size={18} color={corModulo} /> 2. Extratos Bancários Opcionais
                            </h3>
                            <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>
                                Anexe os extratos em formato .xlsx para conciliação bancária.
                            </p>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                            <FileInputBox title="Extrato HUNGER" label="Selecionar .xlsx" inputRef={hungerInputRef} onChange={(e) => setExtratoHunger(e.target.files?.[0] || null)} selectedFile={extratoHunger} />
                            <FileInputBox title="Extrato P&G" label="Selecionar .xlsx" inputRef={pgInputRef} onChange={(e) => setExtratoPg(e.target.files?.[0] || null)} selectedFile={extratoPg} />
                            <FileInputBox title="Extrato GC FOOD" label="Selecionar .xlsx" inputRef={gcInputRef} onChange={(e) => setExtratoGc(e.target.files?.[0] || null)} selectedFile={extratoGc} />
                            <FileInputBox title="Extrato P&M" label="Selecionar .xlsx" inputRef={pmInputRef} onChange={(e) => setExtratoPm(e.target.files?.[0] || null)} selectedFile={extratoPm} />
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            onClick={handleLimpar}
                            disabled={isLoading}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 18px', borderRadius: '8px', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '800', border: '1px solid #cbd5e1', cursor: isLoading ? 'not-allowed' : 'pointer', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}
                        >
                            <Eraser size={18} /> Limpar
                        </button>

                        <button
                            type="submit"
                            disabled={isLoading || arquivosAtos.length === 0}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', backgroundColor: isLoading || arquivosAtos.length === 0 ? '#cbd5e1' : corModulo, color: '#fff', fontWeight: '800', border: 'none', cursor: isLoading || arquivosAtos.length === 0 ? 'not-allowed' : 'pointer', boxShadow: '0 4px 8px -3px rgba(22, 163, 156, 0.45)', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}
                        >
                            {isLoading ? <><Loader2 size={18} className="animate-spin" /> Processando...</> : <><Play size={18} /> Iniciar Conferência PIX</>}
                        </button>
                    </div>
                </form>

                {error && !isLoading && (
                    <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px', fontSize: '14px', fontWeight: '700' }}>
                        <AlertCircle size={20} /> {error}
                    </div>
                )}

                {success && !isLoading && (
                    <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px', fontSize: '14px', fontWeight: '700' }}>
                        <CheckCircle size={20} /> {success}
                    </div>
                )}
            </div>

            {(isLoading || progress > 0) && (
                <div style={cardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {isLoading && <Loader2 size={24} color={corModulo} className="animate-spin" />}
                            {!isLoading && success && <CheckCircle size={24} color="#10b981" />}
                            {!isLoading && error && <AlertCircle size={24} color="#ef4444" />}
                            <div>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>Status da Execução</h4>
                                <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>{statusText}</p>
                            </div>
                        </div>
                        <span style={{ fontSize: '18px', fontWeight: '900', color: getProgressBarColor() }}>{Math.round(progress)}%</span>
                    </div>

                    <div style={{ width: '100%', height: '10px', backgroundColor: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(progress, 100)}%`, height: '100%', backgroundColor: getProgressBarColor(), transition: 'width 0.4s ease' }} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutomacaoConferenciaPIX;
